import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { css } from '@emotion/react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { UnstyledChipSelector } from '../../../components/ChipSelector';
import { KpiPeriod } from '../../../data-models/company-financials.data-model';
import { ISimpleChoice } from '../../../data-models/field2.data-model';
import { FinancialsDateViewPreferences } from '../../../data-models/preferences-v3.data-model';
import { FormFactoryWithStandardLayout } from '../../../components/Form/FormFactory';
import { createFormFromFieldsArray } from '../../../view-models/form.view-model';
import { booleanField } from '../../../data-fields/CommonFields';
import {
  FuturePeriodRangeOptions,
  MAX_RANGE,
  PastPeriodRangeOptions,
  periodRangeToLabel,
} from '../../CompanyProfiles/components/FinancialsDatePickers/financeDatePickerUtils';
import { DetailsConfigActions } from './DetailsConfigActions';
import { ExitConfigBlocker } from './ExitConfigBlocker';
import { ConfigStatus } from './useDisplayConfigState';
import {
  financialsDefaultDateViewOrgState,
  selectedFinancialSettingsFieldsOrgState,
} from './DataDisplayState';

const panelContainer = css`
  display: grid;
  grid-template-columns: 1fr minmax(30%, 26rem);
  column-gap: 1rem;
  overflow: hidden;
`;

const frequencyOptions: ISimpleChoice<KpiPeriod>[] = Object.values(KpiPeriod).map((period) => ({
  value: period,
  displayName: `${period}ly`,
}));

function pastRangeOptionsForFrequency(frequency: KpiPeriod) {
  return PastPeriodRangeOptions[frequency].map((value) => ({
    value,
    displayName: periodRangeToLabel(frequency, value),
  }));
}

function futureRangeOptionsForFrequency(frequency: KpiPeriod) {
  return FuturePeriodRangeOptions[frequency].map((value) => ({
    value,
    displayName: periodRangeToLabel(frequency, value, true),
  }));
}

export function CompanySummaryFinancialSettingsConfig() {
  const [editStatus, setEditStatus] = useState<ConfigStatus>('initial');
  const [selectedFields, setSelectedFields] = useAtom(selectedFinancialSettingsFieldsOrgState);
  const [dateViewPref, setDateViewPref] = useAtom(financialsDefaultDateViewOrgState);

  const selectedFieldsSet = useMemo(() => new Set(selectedFields), [selectedFields]);

  const [localDateView, setLocalDateView] = useState<FinancialsDateViewPreferences>(dateViewPref);

  const methods = useForm<{
    reportedOnly: boolean;
    showBudget: boolean;
    showLT: boolean;
    showYTD: boolean;
    showDeltaTrend: boolean;
  }>({
    mode: 'all',
    defaultValues: {
      reportedOnly: selectedFieldsSet.has('reportedOnly'),
      showBudget: selectedFieldsSet.has('showBudget'),
      showLT: selectedFieldsSet.has('showLT'),
      showYTD: selectedFieldsSet.has('showYTD'),
      showDeltaTrend: selectedFieldsSet.has('showDeltaTrend'),
    },
  });

  const isFormDirty = methods.formState.isDirty;
  const isDateViewDirty =
    localDateView.frequency !== dateViewPref.frequency ||
    localDateView.pastRange !== dateViewPref.pastRange ||
    localDateView.futureRange !== dateViewPref.futureRange;
  const isDirty = isFormDirty || isDateViewDirty;
  const isLocalMaxMode = localDateView.pastRange === MAX_RANGE && localDateView.futureRange === MAX_RANGE;

  useEffect(() => {
    if (isDirty && editStatus !== 'modified') {
      setEditStatus('modified');
    } else if (!isDirty && editStatus === 'modified') {
      setEditStatus('saved');
    }
  }, [editStatus, isDirty]);

  const handleFrequencyChange = useCallback(
    (frequency: KpiPeriod) => {
      setLocalDateView((prev) => {
        if (isLocalMaxMode) {
          return { frequency, pastRange: MAX_RANGE, futureRange: MAX_RANGE };
        }
        const pastOptions = PastPeriodRangeOptions[frequency];
        const pastRange = pastOptions.includes(prev.pastRange) ? prev.pastRange : pastOptions[0];
        const futureOptions = FuturePeriodRangeOptions[frequency];
        const futureRange =
          prev.futureRange != null && futureOptions.includes(prev.futureRange) ? prev.futureRange : null;
        return { frequency, pastRange, futureRange };
      });
    },
    [isLocalMaxMode, setLocalDateView]
  );

  const handlePastRangeChange = useCallback(
    (pastRange: number) => {
      setLocalDateView((prev) => ({
        ...prev,
        pastRange,
        futureRange: isLocalMaxMode ? null : prev.futureRange,
      }));
    },
    [isLocalMaxMode, setLocalDateView]
  );

  const handleFutureRangeChange = useCallback(
    (futureRange: number) => {
      setLocalDateView((prev) => ({
        ...prev,
        pastRange: isLocalMaxMode ? PastPeriodRangeOptions[prev.frequency][0] : prev.pastRange,
        futureRange,
      }));
    },
    [isLocalMaxMode, setLocalDateView]
  );

  const handleMaxToggle = useCallback(() => {
    setLocalDateView((prev) => ({
      ...prev,
      pastRange: MAX_RANGE,
      futureRange: MAX_RANGE,
    }));
  }, [setLocalDateView]);

  const onSave = async () => {
    await handleSubmit();
    setEditStatus('saved');
  };

  const handleSubmit = useCallback(async () => {
    const values = methods.getValues();
    const fields = [];
    if (values.reportedOnly) {
      fields.push('reportedOnly');
    }
    if (values.showBudget) {
      fields.push('showBudget');
    }
    if (values.showLT) {
      fields.push('showLT');
    }
    if (values.showYTD) {
      fields.push('showYTD');
    }
    if (values.showDeltaTrend) {
      fields.push('showDeltaTrend');
    }
    setSelectedFields(fields);
    setDateViewPref(localDateView);
    methods.reset(methods.getValues());
  }, [methods, setSelectedFields, setDateViewPref, localDateView]);

  const currentPastRangeOptions = useMemo(
    () => pastRangeOptionsForFrequency(localDateView.frequency),
    [localDateView.frequency]
  );

  const currentFutureRangeOptions = useMemo(
    () => futureRangeOptionsForFrequency(localDateView.frequency),
    [localDateView.frequency]
  );

  return (
    <FormProvider {...methods}>
      <Stack height='100%'>
        <ExitConfigBlocker onSave={handleSubmit} editStatus={editStatus} />
        <Stack direction='row' alignItems={'start'} columnGap='0.5rem'>
          <Typography variant='h4' mb='1rem'>
            Financial Settings
          </Typography>
        </Stack>
        <Stack direction={'row'} gap={'1rem'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography variant='body2' color='text.secondary'>
            Customize the selected fields for the Company Summary Financial settings section. To apply all
            changes, press the button &quot;Save Changes&quot;
          </Typography>
          <DetailsConfigActions save={{ handler: onSave, disabled: editStatus !== 'modified' }} />
        </Stack>
        <Box margin={'1rem 0'} css={panelContainer}>
          <Stack gap={'1.5rem'}>
            <Stack gap={'1rem'}>
              <Typography variant='subtitle2'>Default Date View</Typography>
              <Typography variant='body2' color='text.secondary'>
                Set the default frequency and range for the Financials tab across all companies.
              </Typography>
              <Stack gap={'0.75rem'}>
                <Typography variant='caption' color='text.secondary'>
                  Frequency
                </Typography>
                <Stack direction='row' gap={'0.5rem'} flexWrap='wrap'>
                  <UnstyledChipSelector
                    options={frequencyOptions}
                    value={localDateView.frequency}
                    onChange={handleFrequencyChange}
                  />
                </Stack>
              </Stack>
              <Stack gap={'0.75rem'}>
                <Typography variant='caption' color='text.secondary'>
                  Range
                </Typography>
                <Chip
                  label='Max'
                  onClick={handleMaxToggle}
                  variant={isLocalMaxMode ? 'filled' : 'outlined'}
                  color={isLocalMaxMode ? 'primary' : 'default'}
                  size='medium'
                  aria-pressed={isLocalMaxMode}
                  sx={{ width: 'fit-content' }}
                />
                <Stack direction='row' gap={'1rem'}>
                  <Stack gap={'0.5rem'}>
                    <UnstyledChipSelector
                      options={currentPastRangeOptions}
                      value={isLocalMaxMode ? null : localDateView.pastRange}
                      onChange={handlePastRangeChange}
                    />
                  </Stack>
                  <Stack gap={'0.5rem'}>
                    <UnstyledChipSelector
                      options={currentFutureRangeOptions}
                      value={isLocalMaxMode ? null : localDateView.futureRange}
                      onChange={handleFutureRangeChange}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
            <Divider />
            <Stack gap={'1rem'}>
              <Typography variant='subtitle2'>Display Options</Typography>
              <CompanySummaryFinancialSettingsForm />
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </FormProvider>
  );
}

const CompanySummaryFinancialSettingsForm = () => {
  const form = useMemo(() => {
    const reportedOnly = booleanField({
      key: 'reportedOnly',
      label: 'Only Reported KPIs',
    });
    const showBudget = booleanField({
      key: 'showBudget',
      label: 'Show Budget',
    });
    const showLT = booleanField({
      key: 'showLT',
      label: 'Show LTM',
    });
    const showYTD = booleanField({
      key: 'showYTD',
      label: 'Show YTD',
    });
    const showDeltaTrend = booleanField({
      key: 'showDeltaTrend',
      label: 'Show Delta Trend',
    });
    return createFormFromFieldsArray([reportedOnly, showBudget, showLT, showYTD, showDeltaTrend]);
  }, []);

  return <FormFactoryWithStandardLayout form={form} />;
};
